import Papa from 'papaparse'
import { Brand, ParsedBrand } from '../types/brand'

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
]

function extractStateFromLocation(location: string): string | undefined {
  if (!location || location === 'unknown') return undefined
  
  // Look for state names in the location string
  const foundState = US_STATES.find(state => 
    location.toLowerCase().includes(state.toLowerCase())
  )
  
  return foundState
}

function parseFoundedYear(year: string | number): number | 'unknown' {
  if (year === 'unknown' || year === '') return 'unknown'
  const parsed = typeof year === 'string' ? parseInt(year, 10) : year
  return isNaN(parsed) ? 'unknown' : parsed
}

function parseSemicolonDelimited(value: string): string[] {
  if (!value || value === 'none' || value === 'unknown') return []
  return value.split(';').map(item => item.trim()).filter(item => item.length > 0)
}

function parseCommaDelimited(value: string): string[] {
  if (!value || value === 'none' || value === 'unknown') return []
  return value.split(',').map(item => item.trim()).filter(item => item.length > 0)
}

function normalizeBrandData(rawBrand: any): ParsedBrand {
  const brand: Brand = {
    brand: rawBrand.brand || '',
    website: rawBrand.website || '',
    amazon_presence: (rawBrand.amazon_presence || 'unknown').toLowerCase(),
    manufacturing_verification: (rawBrand.manufacturing_verification || 'unknown').toLowerCase(),
    hq_location: rawBrand.hq_location || 'unknown',
    mfg_locations: rawBrand.mfg_locations || '',
    primary_products: rawBrand.primary_products || '',
    product_categories: rawBrand.product_categories || '',
    clean_categories: rawBrand.clean_categories || '',
    purchase_methods: rawBrand.purchase_methods || '',
    founded_year: parseFoundedYear(rawBrand.founded_year),
    business_type: rawBrand.business_type || 'unknown',
    certifications: rawBrand.certifications || '',
    price_tier: (rawBrand.price_tier || 'unknown').toLowerCase(),
    consumer_summary: rawBrand.consumer_summary || '',
    evidence_sources: rawBrand.evidence_sources || '',
    confidence_score: parseInt(rawBrand.confidence_score, 10) || 0,
    last_verified: rawBrand.last_verified || ''
  }

  const parsedBrand: ParsedBrand = {
    ...brand,
    product_categories: parseSemicolonDelimited(brand.product_categories),
    clean_categories: parseCommaDelimited(brand.clean_categories),
    mfg_locations: parseSemicolonDelimited(brand.mfg_locations),
    certifications: parseSemicolonDelimited(brand.certifications),
    hq_state: extractStateFromLocation(brand.hq_location),
    founded_decade: typeof brand.founded_year === 'number' ? Math.floor(brand.founded_year / 10) * 10 : undefined
  }

  return parsedBrand
}

export async function loadBrandsFromCSV(csvPath: string = '/brands.csv'): Promise<ParsedBrand[]> {
  try {
    const response = await fetch(csvPath)
    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.statusText}`)
    }
    
    const csvText = await response.text()
    
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false, // We'll handle type conversion manually
        complete: (results) => {
          try {
            if (results.errors.length > 0) {
              console.warn('CSV parsing warnings:', results.errors)
            }
            
            const parsedBrands = results.data.map(normalizeBrandData)
            console.log(`Loaded ${parsedBrands.length} brands from CSV`)
            resolve(parsedBrands)
          } catch (error) {
            reject(new Error(`Failed to process CSV data: ${error}`))
          }
        },
        error: (error: any) => {
          reject(new Error(`CSV parsing error: ${error.message}`))
        }
      })
    })
  } catch (error) {
    throw new Error(`Failed to load CSV: ${error}`)
  }
}

export function calculateKPIs(brands: ParsedBrand[]) {
  const totalBrands = brands.length
  const confirmedUSA = brands.filter(b => b.manufacturing_verification === 'confirmed_usa').length
  const onAmazon = brands.filter(b => b.amazon_presence !== 'none' && b.amazon_presence !== 'unknown').length
  
  const confidenceScores = brands.map(b => b.confidence_score).filter(s => s > 0)
  const medianConfidenceScore = confidenceScores.length > 0 
    ? confidenceScores.sort((a, b) => a - b)[Math.floor(confidenceScores.length / 2)] ?? 0
    : 0
  
  const foundedYears = brands
    .map(b => b.founded_year)
    .filter((year): year is number => typeof year === 'number')
  
  const oldestFoundedYear = foundedYears.length > 0 ? Math.min(...foundedYears) : 0
  const newestFoundedYear = foundedYears.length > 0 ? Math.max(...foundedYears) : 0
  
  // Debug logging for year range
  console.log('Founded year range:', oldestFoundedYear, '-', newestFoundedYear)
  console.log('Total brands with valid years:', foundedYears.length)

  return {
    totalBrands,
    confirmedUSAPercent: totalBrands > 0 ? (confirmedUSA / totalBrands) * 100 : 0,
    amazonPresencePercent: totalBrands > 0 ? (onAmazon / totalBrands) * 100 : 0,
    medianConfidenceScore,
    oldestFoundedYear,
    newestFoundedYear
  }
}